import { Box, Text, TextInput, UnstyledButton } from '@mantine/core';
import { useDebouncedCallback } from '@mantine/hooks';
import {
	AnalyticsEvent,
	AnalyticsEventType,
	AnalyticsJourney,
	AnalyticsStage,
	analyticsSdk
} from '@newo/analytics';
import { X } from 'lucide-react';
import { type FC, useCallback, useRef } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useHostContext } from '@/context/HostContext.tsx';
import {
	type StepperOnboardingFormValues,
	StepperSetupStepKey
} from '@/stepper/types.ts';
import { STEP_CONTENT_CLASS_NAMES } from '../../classNames';
import StepperWebsiteStepActions from './components/StepperWebsiteStepActions';

interface StepperWebsiteStepContentProps {
	isLocked?: boolean;
	isTrainingPending: boolean;
	onTrain: () => void;
}

const StepperWebsiteStepContent: FC<StepperWebsiteStepContentProps> = ({
	isLocked = false,
	isTrainingPending,
	onTrain
}) => {
	const { localization } = useHostContext();
	const { control, setValue } = useFormContext<StepperOnboardingFormValues>();
	const websiteUrl = useWatch({
		control,
		name: 'websiteUrl'
	});
	const previousWebsiteUrlRef = useRef(websiteUrl);

	const debouncedWebsiteUrlChange = useDebouncedCallback(
		(nextWebsiteUrl: string) => {
			if (!nextWebsiteUrl.trim()) {
				return;
			}

			analyticsSdk.track(AnalyticsEvent.FieldChanged, {
				field: 'websiteUrl',
				journey: AnalyticsJourney.CreatorSetup,
				stage: AnalyticsStage.CreatorOnboarding,
				step: StepperSetupStepKey.ExtractWebsiteContent,
				type: AnalyticsEventType.Field,
				website_url: nextWebsiteUrl.trim()
			});
		},
		600
	);

	const handleWebsiteUrlChange = useCallback(
		(nextWebsiteUrl: string) => {
			const previousWebsiteUrl = previousWebsiteUrlRef.current.trim();
			previousWebsiteUrlRef.current = nextWebsiteUrl;

			if (!nextWebsiteUrl.trim() && previousWebsiteUrl) {
				analyticsSdk.track(AnalyticsEvent.FieldCleared, {
					field: 'websiteUrl',
					journey: AnalyticsJourney.CreatorSetup,
					previous_website_url: previousWebsiteUrl,
					stage: AnalyticsStage.CreatorOnboarding,
					step: StepperSetupStepKey.ExtractWebsiteContent,
					type: AnalyticsEventType.Field
				});
			}

			debouncedWebsiteUrlChange(nextWebsiteUrl);
		},
		[debouncedWebsiteUrlChange]
	);

	const handleWebsiteFocus = useCallback(() => {
		analyticsSdk.track(AnalyticsEvent.FieldFocused, {
			field: 'websiteUrl',
			journey: AnalyticsJourney.CreatorSetup,
			stage: AnalyticsStage.CreatorOnboarding,
			step: StepperSetupStepKey.ExtractWebsiteContent,
			type: AnalyticsEventType.Field
		});
	}, []);

	const handleClearWebSite = () => {
		if (isLocked) {
			return;
		}

		handleWebsiteUrlChange('');
		setValue('websiteUrl', '', {
			shouldDirty: true,
			shouldTouch: true,
			shouldValidate: true
		});
	};

	const handleTrainClick = useCallback(() => {
		analyticsSdk.track(AnalyticsEvent.ButtonClicked, {
			action: 'train',
			field: 'websiteUrl',
			journey: AnalyticsJourney.CreatorSetup,
			stage: AnalyticsStage.CreatorOnboarding,
			step: StepperSetupStepKey.ExtractWebsiteContent,
			type: AnalyticsEventType.Interaction,
			website_url: websiteUrl.trim()
		});
		onTrain();
	}, [onTrain, websiteUrl]);

	return (
		<Box className={STEP_CONTENT_CLASS_NAMES.stepBody}>
			<Text className={STEP_CONTENT_CLASS_NAMES.stepSubtitle}>
				{localization.translate(
					'creator',
					'stepperOnboarding.steps.extractWebsiteContent.description'
				)}
			</Text>
			<Box className={STEP_CONTENT_CLASS_NAMES.formStack}>
				<Controller
					control={control}
					name="websiteUrl"
					render={({ field, fieldState }) => (
						<TextInput
							{...field}
							classNames={{
								error: 'mt-1 text-sm leading-5 text-[#D92D20]',
								input: STEP_CONTENT_CLASS_NAMES.input,
								label: STEP_CONTENT_CLASS_NAMES.formLabel,
								root: 'w-full'
							}}
							data-testid="stepper-website-input"
							error={fieldState.error?.message}
							onChange={(event) => {
								handleWebsiteUrlChange(event.currentTarget.value);
								field.onChange(event);
							}}
							onFocus={handleWebsiteFocus}
							placeholder={localization.translate(
								'creator',
								'stepperOnboarding.steps.extractWebsiteContent.websitePlaceholder'
							)}
							readOnly={isLocked}
							rightSection={
								field.value?.trim() && !isLocked ? (
									<UnstyledButton
										aria-label={localization.translate(
											'creator',
											'stepperOnboarding.steps.extractWebsiteContent.clear'
										)}
										className="inline-flex h-5 w-5 items-center justify-center"
										data-testid="stepper-website-clear-button"
										onClick={handleClearWebSite}
										type="button"
									>
										<X
											aria-hidden="true"
											className="h-3 w-3 text-[var(--color-primary-900)]"
										/>
									</UnstyledButton>
								) : undefined
							}
						/>
					)}
				/>
			</Box>

			{/* TODO: the "I don't have a website" / "I don't want to provide a website"
			actions are removed; StepperBusinessStepContent is kept to bring them back. */}
			<StepperWebsiteStepActions
				isLocked={isLocked}
				isTrainingPending={isTrainingPending}
				onTrain={handleTrainClick}
			/>
		</Box>
	);
};

export default StepperWebsiteStepContent;
